import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  batchCorrectionLookupDispatch,
  batchCorrectionLookupBatch,
  batchCorrectionImpactPreview,
  batchCorrectionApply,
  batchCorrectionHistory,
  batchCorrectionZipPdf,
  batchCorrectionBulkEmail,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertTriangle,
  ArrowLeft,
  Search,
  Loader2,
  CheckCircle2,
  History,
  ShieldAlert,
  Download,
  Mail,
  FileEdit,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin-only Batch Correction Tool.
 * Permission: super_admin OR can_correct_batch_data flag.
 */
export default function BatchCorrections() {
  const navigate = useNavigate();

  // Lookup state
  const [activeTab, setActiveTab] = useState('by-dispatch');
  const [dispatchQuery, setDispatchQuery] = useState('');
  const [batchQuery, setBatchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [dispatchResult, setDispatchResult] = useState(null);   // {id, lines:[...]}
  const [batchResult, setBatchResult] = useState(null);         // {matches:[...]}

  // Edits keyed by `${dispatch_id}::${line_index}` →
  //   { new_batch_code?: string, new_use_by_date?: string (yyyy-mm-dd) }
  const [edits, setEdits] = useState({});
  const [reason, setReason] = useState('');
  const [propagate, setPropagate] = useState(true);
  // Impact preview keyed the same way edits are: `${dispatch_id}::${line_index}`
  // → { sibling_dispatch_count, sibling_line_count, sibling_dispatches }
  const [impactByKey, setImpactByKey] = useState({});
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Post-apply result + bulk distribution
  const [applyResult, setApplyResult] = useState(null);   // {affected_dispatch_ids, records, run_id}
  const [zipping, setZipping] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailExtra, setEmailExtra] = useState('');

  // History
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await batchCorrectionHistory(100);
      setHistory(data.records || []);
    } catch (err) {
      // Likely 403 — non-permitted users see this card but get blocked
      if (err?.response?.status === 403) {
        toast.error(err?.response?.data?.detail || 'You do not have permission for this tool');
        navigate('/admin/data-management');
      }
    }
  }, [navigate]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const resetEdits = () => {
    setEdits({});
    setReason('');
    setApplyResult(null);
  };

  // ── Lookups ─────────────────────────────────────────────────────────
  const handleLookupDispatch = async () => {
    if (!dispatchQuery.trim()) {
      toast.error('Enter a dispatch number first');
      return;
    }
    setLoading(true);
    setDispatchResult(null);
    resetEdits();
    try {
      const { data } = await batchCorrectionLookupDispatch(dispatchQuery.trim());
      setDispatchResult(data);
      toast.success(`Loaded dispatch ${data.dispatch_number} (${data.lines.length} lines)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Dispatch not found');
    } finally {
      setLoading(false);
    }
  };

  const handleLookupBatch = async () => {
    if (!batchQuery.trim()) {
      toast.error('Enter a batch code first');
      return;
    }
    setLoading(true);
    setBatchResult(null);
    resetEdits();
    try {
      const { data } = await batchCorrectionLookupBatch(batchQuery.trim());
      setBatchResult(data);
      if (data.total_matches === 0) {
        toast.info(`Batch code "${data.batch_code}" not found on any dispatch`);
      } else if (data.has_drift) {
        toast.warning(`Drift detected: ${data.distinct_use_by_dates.length} different use-by dates found for this batch!`);
      } else {
        toast.success(`Found ${data.total_matches} use(s) of batch "${data.batch_code}"`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Edits ───────────────────────────────────────────────────────────
  const editKey = (dispatchId, lineIndex) => `${dispatchId}::${lineIndex}`;

  const setEdit = (dispatchId, lineIndex, field, value) => {
    const key = editKey(dispatchId, lineIndex);
    setEdits(prev => {
      const current = prev[key] || {};
      const next = { ...current, [field]: value };
      // Drop the row if both fields are empty
      if (!next.new_batch_code && !next.new_use_by_date) {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      }
      return { ...prev, [key]: next };
    });
    // Lazy-fetch propagation impact the first time this row gets touched.
    // Cached afterwards so we don't spam the endpoint on every keystroke.
    setImpactByKey(prev => {
      if (prev[key] !== undefined) return prev;
      // Fire-and-forget; result lands when the promise resolves.
      batchCorrectionImpactPreview(dispatchId, lineIndex)
        .then(({ data }) => setImpactByKey(p => ({ ...p, [key]: data })))
        .catch(() => setImpactByKey(p => ({ ...p, [key]: { sibling_line_count: 0, sibling_dispatch_count: 0 } })));
      return { ...prev, [key]: 'loading' };
    });
  };

  const pendingCount = Object.keys(edits).length;
  const totalSiblingLines = Object.entries(edits).reduce((sum, [k]) => {
    const imp = impactByKey[k];
    if (!imp || imp === 'loading') return sum;
    return sum + (imp.sibling_line_count || 0);
  }, 0);
  const totalSiblingDispatches = (() => {
    const ids = new Set();
    for (const k of Object.keys(edits)) {
      const imp = impactByKey[k];
      if (!imp || imp === 'loading') continue;
      (imp.sibling_dispatches || []).forEach(s => ids.add(s.dispatch_id));
    }
    return ids.size;
  })();

  // ── Apply ───────────────────────────────────────────────────────────
  const buildPayload = () => {
    return Object.entries(edits).map(([key, val]) => {
      const [dispatch_id, idx] = key.split('::');
      const out = { dispatch_id, line_index: parseInt(idx, 10) };
      if (val.new_batch_code !== undefined && val.new_batch_code !== null && val.new_batch_code !== '') {
        out.new_batch_code = val.new_batch_code.trim();
      }
      if (val.new_use_by_date !== undefined && val.new_use_by_date !== null && val.new_use_by_date !== '') {
        out.new_use_by_date = val.new_use_by_date;
      }
      return out;
    }).filter(c => c.new_batch_code !== undefined || c.new_use_by_date !== undefined);
  };

  const handleApply = async () => {
    if (reason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters (audit requirement)');
      return;
    }
    const payload = buildPayload();
    if (payload.length === 0) {
      toast.error('No changes to apply');
      return;
    }
    setApplying(true);
    try {
      const { data } = await batchCorrectionApply(payload, reason.trim(), propagate);
      setApplyResult(data);
      setConfirmOpen(false);
      setEdits({});
      setReason('');
      setImpactByKey({});
      await loadHistory();
      if (data.applied === 0) {
        toast.info('No changes applied — values already matched');
      } else {
        const propMsg = data.propagated > 0
          ? ` (${data.manual} direct + ${data.propagated} auto-propagated)`
          : '';
        toast.success(
          `Applied ${data.applied} correction(s) across ${data.affected_dispatch_ids.length} dispatch(es)${propMsg}`
        );
      }
      // Refresh whatever lookup is currently shown so the table updates
      if (activeTab === 'by-dispatch' && dispatchResult) {
        const { data: fresh } = await batchCorrectionLookupDispatch(dispatchResult.dispatch_number);
        setDispatchResult(fresh);
      } else if (activeTab === 'by-batch' && batchResult) {
        const { data: fresh } = await batchCorrectionLookupBatch(batchQuery.trim());
        setBatchResult(fresh);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  // ── Distribution actions on the just-applied set ────────────────────
  const handleDownloadZip = async () => {
    if (!applyResult?.affected_dispatch_ids?.length) return;
    setZipping(true);
    try {
      const response = await batchCorrectionZipPdf(applyResult.affected_dispatch_ids);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Corrected_Dispatches_${new Date().toISOString().slice(0, 16).replace(/[T:]/g, '')}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('ZIP downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'ZIP download failed');
    } finally {
      setZipping(false);
    }
  };

  const handleBulkEmail = async () => {
    if (!applyResult?.affected_dispatch_ids?.length) return;
    setEmailing(true);
    try {
      const { data } = await batchCorrectionBulkEmail(applyResult.affected_dispatch_ids, emailExtra);
      const ok = data.success || 0;
      const fail = (data.results || []).filter(r => !r.ok).length;
      if (fail === 0) {
        toast.success(`Sent ${ok}/${data.total} emails successfully`);
      } else {
        const firstErr = (data.results || []).find(r => !r.ok)?.error || 'unknown';
        toast.error(`${ok}/${data.total} sent · ${fail} failed. First error: ${firstErr}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk email failed');
    } finally {
      setEmailing(false);
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────
  const renderEditableLine = (line, dispatchId) => {
    const key = editKey(dispatchId, line.line_index ?? line.index);
    const edit = edits[key] || {};
    const idx = line.line_index ?? line.index;
    const hasEdit = !!(edit.new_batch_code || edit.new_use_by_date);
    return (
      <tr key={key} className={`border-b hover:bg-zinc-50 ${hasEdit ? 'bg-blue-50/60' : ''}`}>
        <td className="px-3 py-2 text-sm text-zinc-700">{idx + 1}</td>
        <td className="px-3 py-2 text-sm">
          <div className="font-medium">{line.product_name || '-'}</div>
          <div className="text-xs text-zinc-500">{line.supplier_product_code || ''}</div>
        </td>
        <td className="px-3 py-2 text-sm">
          <div className="text-xs text-zinc-500 mb-0.5">
            Current: <span className="font-mono font-medium text-zinc-800">{line.batch_code || '—'}</span>
          </div>
          <div className="relative">
            <Input
              data-testid={`bc-batch-${idx}`}
              placeholder="Enter new batch code…"
              value={edit.new_batch_code ?? ''}
              onChange={(e) => setEdit(dispatchId, idx, 'new_batch_code', e.target.value)}
              className="h-8 w-40 text-sm pl-7 border-blue-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            <FileEdit className="h-3.5 w-3.5 text-blue-500 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </td>
        <td className="px-3 py-2 text-sm">
          <div className="text-xs text-zinc-500 mb-0.5">
            Current: <span className="font-mono font-medium text-zinc-800">{line.use_by_date || '—'}</span>
          </div>
          <div className="relative">
            <Input
              data-testid={`bc-useby-${idx}`}
              type="date"
              value={edit.new_use_by_date ?? ''}
              onChange={(e) => setEdit(dispatchId, idx, 'new_use_by_date', e.target.value)}
              className="h-8 w-44 text-sm border-blue-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </td>
        <td className="px-3 py-2 text-sm text-zinc-600">{line.quantity ?? '—'}</td>
        <td className="px-3 py-2 text-sm text-zinc-600">
          {(line.total_weight ?? 0).toFixed ? line.total_weight.toFixed(2) : line.total_weight || '—'}
        </td>
      </tr>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="batch-corrections-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/data-management')} data-testid="btn-back">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Data Management
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Batch Correction Tool</h1>
            <p className="text-sm text-zinc-500">Retroactive batch code / use-by date corrections — admin only, fully audit-logged</p>
          </div>
        </div>
        <Button
          variant={history.length > 0 ? 'default' : 'outline'}
          size="sm"
          onClick={() => setHistoryOpen(true)}
          data-testid="btn-open-history"
          className={history.length > 0 ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
        >
          <History className="h-4 w-4 mr-1" />
          Audit History
          <Badge
            variant="secondary"
            className={`ml-2 ${history.length > 0 ? 'bg-white text-emerald-800' : ''}`}
            data-testid="badge-history-count"
          >
            {history.length}
          </Badge>
        </Button>
      </div>

      {/* Inline recent corrections strip — makes audit tracking visible without opening the dialog */}
      {history.length > 0 && (
        <Card className="border-zinc-200" data-testid="recent-corrections-strip">
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-zinc-700 flex items-center gap-2">
                <History className="h-4 w-4 text-zinc-500" />
                Recent Corrections (last {Math.min(3, history.length)} of {history.length})
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setHistoryOpen(true)}
                data-testid="btn-view-full-history"
                className="text-xs h-7"
              >
                View full ledger →
              </Button>
            </div>
          </CardHeader>
          <CardContent className="py-2">
            <div className="divide-y divide-zinc-100">
              {history.slice(0, 3).map(r => {
                const isFresh = applyResult && r.run_id === applyResult.run_id;
                return (
                  <div
                    key={r.id}
                    className={`py-2 text-xs grid grid-cols-12 gap-2 items-center ${isFresh ? 'bg-emerald-50 -mx-3 px-3 rounded' : ''}`}
                    data-testid={`recent-correction-${r.id}`}
                  >
                    <div className="col-span-2 text-zinc-500" title={r.created_at}>
                      {new Date(r.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div className="col-span-2 font-medium">
                      #{r.dispatch_number}
                      {r.auto_propagated && (
                        <Badge variant="outline" className="ml-1 text-[10px] py-0 px-1 border-amber-400 text-amber-700">auto</Badge>
                      )}
                    </div>
                    <div className="col-span-3 text-zinc-700 truncate" title={r.product_name}>
                      {r.product_name}
                    </div>
                    <div className="col-span-4 text-zinc-600">
                      {r.before?.batch_code !== r.after?.batch_code && (
                        <span className="mr-2">
                          <span className="text-zinc-400 line-through">{r.before?.batch_code}</span>
                          {' → '}
                          <span className="font-mono font-medium text-emerald-700">{r.after?.batch_code}</span>
                        </span>
                      )}
                      {r.before?.use_by_date !== r.after?.use_by_date && (
                        <span>
                          <span className="text-zinc-400 line-through">{r.before?.use_by_date}</span>
                          {' → '}
                          <span className="font-mono font-medium text-emerald-700">{r.after?.use_by_date}</span>
                        </span>
                      )}
                    </div>
                    <div className="col-span-1 text-right text-[10px] text-zinc-500">
                      {r.user_email?.split('@')[0]}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">Restricted tool — fully audit-logged.</p>
          <p className="mt-1">
            Every change is written immutably to the <code>batch_corrections</code> ledger with a chain hash, plus an
            entry in the audit log. Provide a clear reason on every correction so inspectors can trace what was
            changed and why. Affected dispatches can be ZIP-downloaded and re-emailed to the customer after save.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); resetEdits(); }}>
        <TabsList className="grid grid-cols-2 max-w-md">
          <TabsTrigger value="by-dispatch" data-testid="tab-by-dispatch">
            <Search className="h-4 w-4 mr-1" /> By Dispatch #
          </TabsTrigger>
          <TabsTrigger value="by-batch" data-testid="tab-by-batch">
            <FileEdit className="h-4 w-4 mr-1" /> By Batch Code
          </TabsTrigger>
        </TabsList>

        {/* ── BY DISPATCH ── */}
        <TabsContent value="by-dispatch" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Lookup by Dispatch Number</CardTitle>
              <CardDescription>Enter the dispatch number (e.g. 202600100) or its internal ID.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  data-testid="input-dispatch-number"
                  placeholder="e.g. 202600100"
                  value={dispatchQuery}
                  onChange={(e) => setDispatchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLookupDispatch()}
                  className="max-w-xs"
                />
                <Button onClick={handleLookupDispatch} disabled={loading} data-testid="btn-lookup-dispatch">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Lookup'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {dispatchResult && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Dispatch #{dispatchResult.dispatch_number} — {dispatchResult.customer_name}
                  <Badge variant="outline" className="ml-2">{dispatchResult.status}</Badge>
                </CardTitle>
                <CardDescription>
                  PO: {dispatchResult.po_number || '—'} · Date: {dispatchResult.dispatch_date || '—'}
                </CardDescription>
                <div className="mt-2 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-900">
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>How to use:</strong> Click into any <span className="inline-flex items-center gap-1 px-1 py-0.5 rounded border border-blue-300 bg-white"><FileEdit className="h-3 w-3 text-blue-500" /> highlighted field</span> below and type the corrected value. The <em>current</em> value stays visible above each field for reference. A <strong>Review &amp; Apply</strong> bar will appear at the bottom of the page once you start typing.
                  </div>
                </div>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-100 text-left">
                    <tr>
                      <th className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-600">#</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-600">Product</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-600">Batch Code</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-600">Use-by Date</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-600">Qty</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-600">Weight (kg)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dispatchResult.lines.map(l => renderEditableLine(l, dispatchResult.id))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── BY BATCH ── */}
        <TabsContent value="by-batch" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Lookup by Batch Code</CardTitle>
              <CardDescription>
                Finds every dispatch where this batch code appears. Warns if more than one
                use-by date was entered for the same batch (drift).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  data-testid="input-batch-code"
                  placeholder="e.g. 903420"
                  value={batchQuery}
                  onChange={(e) => setBatchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLookupBatch()}
                  className="max-w-xs"
                />
                <Button onClick={handleLookupBatch} disabled={loading} data-testid="btn-lookup-batch">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Lookup'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {batchResult && batchResult.matches.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Batch &quot;{batchResult.batch_code}&quot; — {batchResult.total_matches} use(s)
                  {batchResult.has_drift && (
                    <Badge variant="destructive" className="ml-2">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Drift: {batchResult.distinct_use_by_dates.join(', ')}
                    </Badge>
                  )}
                </CardTitle>
                <div className="mt-2 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-900">
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>How to use:</strong> Click into any <span className="inline-flex items-center gap-1 px-1 py-0.5 rounded border border-blue-300 bg-white"><FileEdit className="h-3 w-3 text-blue-500" /> highlighted field</span> below and type the corrected value. The <em>current</em> value stays visible above each field. Fixing the use-by date here will cascade across every dispatch using this batch when <strong>Propagate</strong> is enabled. A <strong>Review &amp; Apply</strong> bar will appear once you start typing.
                  </div>
                </div>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-100 text-left">
                    <tr>
                      <th className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-600">Dispatch</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-600">Customer</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-600">Line</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-600">Product</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-600">Batch Code</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-600">Use-by Date</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-600">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchResult.matches.map((m) => {
                      const key = editKey(m.dispatch_id, m.line_index);
                      const edit = edits[key] || {};
                      const hasEdit = !!(edit.new_batch_code || edit.new_use_by_date);
                      return (
                        <tr key={key} className={`border-b hover:bg-zinc-50 ${hasEdit ? 'bg-blue-50/60' : ''}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium">#{m.dispatch_number}</div>
                            <div className="text-xs text-zinc-500">{m.dispatch_date} · {m.status}</div>
                          </td>
                          <td className="px-3 py-2 text-sm">{m.customer_name}</td>
                          <td className="px-3 py-2 text-sm text-zinc-600">{m.line_index + 1}</td>
                          <td className="px-3 py-2 text-sm">
                            {m.product_name}
                            <div className="text-xs text-zinc-500">{m.supplier_product_code || ''}</div>
                          </td>
                          <td className="px-3 py-2 text-sm">
                            <div className="text-xs text-zinc-500 mb-0.5">
                              Current: <span className="font-mono font-medium text-zinc-800">{m.batch_code}</span>
                            </div>
                            <div className="relative">
                              <Input
                                data-testid={`bc-batch-${m.dispatch_id}-${m.line_index}`}
                                placeholder="Enter new batch code…"
                                value={edit.new_batch_code ?? ''}
                                onChange={(e) => setEdit(m.dispatch_id, m.line_index, 'new_batch_code', e.target.value)}
                                className="h-8 w-40 text-sm pl-7 border-blue-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              />
                              <FileEdit className="h-3.5 w-3.5 text-blue-500 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-sm">
                            <div className="text-xs text-zinc-500 mb-0.5">
                              Current: <span className="font-mono font-medium text-zinc-800">{m.use_by_date || '—'}</span>
                            </div>
                            <div className="relative">
                              <Input
                                data-testid={`bc-useby-${m.dispatch_id}-${m.line_index}`}
                                type="date"
                                value={edit.new_use_by_date ?? ''}
                                onChange={(e) => setEdit(m.dispatch_id, m.line_index, 'new_use_by_date', e.target.value)}
                                className="h-8 w-44 text-sm border-blue-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-sm">{m.quantity ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Passive placeholder: shown when there are lookup results but no edits yet ── */}
      {pendingCount === 0 && (dispatchResult || (batchResult && batchResult.matches.length > 0)) && (
        <Card className="border-dashed border-zinc-300 bg-zinc-50/60">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 text-sm text-zinc-600">
                <FileEdit className="h-4 w-4 text-zinc-400" />
                <span>Waiting for edits — type a new batch code or use-by date in a highlighted field above.</span>
              </div>
              <Button variant="outline" disabled data-testid="btn-review-apply-placeholder">
                Review &amp; Apply
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Apply control bar ── */}
      {pendingCount > 0 && (
        <Card className="border-blue-300 bg-blue-50/40 sticky bottom-4 shadow-lg z-10">
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <FileEdit className="h-5 w-5 text-blue-700" />
                <span className="font-medium text-blue-900">
                  {pendingCount} pending change{pendingCount !== 1 ? 's' : ''}
                </span>
                {totalSiblingLines > 0 && propagate && (
                  <Badge variant="secondary" className="ml-2">
                    +{totalSiblingLines} sibling line(s) across {totalSiblingDispatches} other dispatch(es)
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetEdits} data-testid="btn-discard-edits">
                  Discard
                </Button>
                <Button onClick={() => setConfirmOpen(true)} data-testid="btn-open-confirm">
                  Review & Apply
                </Button>
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm text-blue-900 bg-white/60 rounded p-2 border border-blue-200">
              <input
                type="checkbox"
                checked={propagate}
                onChange={(e) => setPropagate(e.target.checked)}
                data-testid="checkbox-propagate"
                className="mt-0.5"
              />
              <span>
                <strong>Propagate to all dispatches using this batch</strong> (recommended).
                When checked, the correction also updates every other dispatch line that references the same
                batch code + product, keeping recall reports, traceability, batch register, dashboard
                expiring-stock view and pallet labels all in sync. Each cascade is chain-hashed and audit-logged
                with <code>auto_propagated=true</code>.
                {totalSiblingLines > 0 && propagate && (
                  <span className="block mt-1 text-blue-700">
                    Will also touch <strong>{totalSiblingLines}</strong> sibling line(s)
                    across <strong>{totalSiblingDispatches}</strong> other dispatch(es).
                  </span>
                )}
              </span>
            </label>

            <div>
              <Label htmlFor="reason-input" className="text-sm">
                Reason for correction (min 10 chars) — visible to auditors
              </Label>
              <Textarea
                id="reason-input"
                data-testid="input-reason"
                placeholder="e.g. Incorrect use-by date entered by staff on 24/Apr — verified against batch register"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="mt-1"
              />
              <p className="text-xs text-zinc-500 mt-1">{reason.length}/500 characters</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Post-apply distribution ── */}
      {applyResult && applyResult.affected_dispatch_ids?.length > 0 && (
        <Card className="border-emerald-300 bg-emerald-50/30">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              {applyResult.applied} correction(s) applied across {applyResult.affected_dispatch_ids.length} dispatch(es)
              {applyResult.propagated > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {applyResult.manual} direct + {applyResult.propagated} auto-propagated
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="flex items-center gap-2 flex-wrap">
              <span>Run ID: <code className="text-xs">{applyResult.run_id}</code></span>
              <Badge variant="outline" className="border-emerald-400 text-emerald-800 text-[10px]">
                <History className="h-3 w-3 mr-1" /> Recorded to audit ledger
              </Badge>
              <Button
                variant="link"
                size="sm"
                className="h-5 px-1 text-emerald-800 underline"
                onClick={() => setHistoryOpen(true)}
                data-testid="btn-view-in-history"
              >
                View in History →
              </Button>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-sm">Optional extra message (added to customer email)</Label>
              <Textarea
                data-testid="input-email-extra"
                rows={2}
                value={emailExtra}
                onChange={(e) => setEmailExtra(e.target.value)}
                placeholder="e.g. Apologies for the inconvenience — please discard the prior copy."
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={handleDownloadZip} disabled={zipping} data-testid="btn-download-zip">
                {zipping ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                Download All as ZIP
              </Button>
              <Button onClick={handleBulkEmail} disabled={emailing} variant="default" data-testid="btn-bulk-email">
                {emailing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
                Email Customers
              </Button>
              <Button variant="outline" onClick={() => setApplyResult(null)} data-testid="btn-dismiss-apply-result">
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Confirm dialog ── */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirm Batch Corrections</DialogTitle>
            <DialogDescription>
              These {pendingCount} change(s) will be applied immediately and written to the audit ledger.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[40vh] overflow-y-auto space-y-2 my-2">
            {Object.entries(edits).map(([key, val]) => {
              const [did, idx] = key.split('::');
              return (
                <div key={key} className="text-sm bg-zinc-50 rounded p-2 border">
                  <div className="font-mono text-xs text-zinc-500">{did} → line #{parseInt(idx, 10) + 1}</div>
                  {val.new_batch_code && <div>batch_code → <strong>{val.new_batch_code}</strong></div>}
                  {val.new_use_by_date && <div>use_by_date → <strong>{val.new_use_by_date}</strong></div>}
                </div>
              );
            })}
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800 flex gap-2">
            <Info className="h-4 w-4 flex-shrink-0" />
            <span>Reason: &quot;{reason}&quot;</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleApply} disabled={applying} data-testid="btn-confirm-apply">
              {applying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Apply {pendingCount} Correction(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── History dialog ── */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Correction History (most recent {history.length})</DialogTitle>
            <DialogDescription>
              Immutable chain-hashed ledger. Each entry has both audit log and chain hash for tamper detection.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left text-xs uppercase">When</th>
                  <th className="px-2 py-2 text-left text-xs uppercase">User</th>
                  <th className="px-2 py-2 text-left text-xs uppercase">Dispatch</th>
                  <th className="px-2 py-2 text-left text-xs uppercase">Product</th>
                  <th className="px-2 py-2 text-left text-xs uppercase">Before</th>
                  <th className="px-2 py-2 text-left text-xs uppercase">After</th>
                  <th className="px-2 py-2 text-left text-xs uppercase">Reason</th>
                </tr>
              </thead>
              <tbody>
                {history.map(r => (
                  <tr key={r.id} className="border-b">
                    <td className="px-2 py-2 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-2 py-2 text-xs">{r.user_email}</td>
                    <td className="px-2 py-2 text-xs">
                      <div className="flex items-center gap-1">
                        #{r.dispatch_number}
                        {r.auto_propagated && (
                          <Badge variant="outline" className="text-[10px] py-0 px-1 border-amber-400 text-amber-700">
                            auto
                          </Badge>
                        )}
                      </div>
                      <div className="text-zinc-500">{r.customer_name}</div>
                    </td>
                    <td className="px-2 py-2 text-xs">{r.product_name}</td>
                    <td className="px-2 py-2 text-xs">
                      {r.before?.batch_code && <div>batch: {r.before.batch_code}</div>}
                      {r.before?.use_by_date && <div>use_by: {r.before.use_by_date}</div>}
                    </td>
                    <td className="px-2 py-2 text-xs">
                      {r.after?.batch_code && <div className="font-medium">batch: {r.after.batch_code}</div>}
                      {r.after?.use_by_date && <div className="font-medium">use_by: {r.after.use_by_date}</div>}
                    </td>
                    <td className="px-2 py-2 text-xs max-w-xs truncate" title={r.reason}>{r.reason}</td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr><td colSpan="7" className="px-2 py-6 text-center text-zinc-500">No corrections recorded yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
